import dayjs from '../../utils/dayjs';
import { Form, FormLayout, Icon, InlineStack, Modal, Popover, Tag, TextField, Text, Select, Card, DatePicker } from '@shopify/polaris';
import { useField, useForm } from '@shopify/react-form';
import helpers from 'helpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ENUM_VEHICLE_TRIP_TYPE,
  TypedVehicle_log,
  useGetVehiclelog,
  useMutateCreateVehiclelog,
  useUpdateVehiclelog,
} from 'queries/vehicle_log.query';
import { useNotification } from 'context/NotificationContext';
import __ from 'languages/index';
import { CalendarIcon, PlusIcon } from '@shopify/polaris-icons';
import QuickSearchUser from 'components/quickSearchUser';


// --- Helper Functions ---
// Chuyển đổi object Date sang chuỗi 'YYYY-MM-DD'
function dateToYyyyMmDd(date) {
  const offset = date.getTimezoneOffset();
  const adjustedDate = new Date(date.getTime() - offset * 60 * 1000);
  return adjustedDate.toISOString().split('T')[0];
}

// --- Reusable Date Picker Component ---
// Component này kết hợp TextField, Popover và DatePicker
function DatePickerInput({ label, field, connectedLeft }) {
  const [visible, setVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(field.value ? new Date(field.value) : new Date());

  const [{ month, year }, setDate] = useState({
    month: selectedDate.getMonth(),
    year: selectedDate.getFullYear(),
  });

  const handleDateChange = useCallback(
    (date) => {
      setSelectedDate(date);
      field.onChange(dateToYyyyMmDd(date));
      setVisible(false);
    },
    [field]
  );

  const handleMonthChange = useCallback((month, year) => {
    setDate({ month, year });
  }, []);

  const activator = (
    <TextField
      {...field}
      label={label}
      onFocus={() => setVisible(true)}
      prefix={<Icon source={CalendarIcon} />}
      autoComplete="off"
      clearButton
      onClearButtonClick={() => field.onChange('')}
      connectedLeft={connectedLeft}
    />
  );

  return (
    <Popover preferredPosition="below" active={visible} activator={activator} onClose={() => setVisible(false)}>
      <Card>
        <DatePicker
          month={month}
          year={year}
          onChange={({ start }) => handleDateChange(start)}
          onMonthChange={handleMonthChange}
          selected={field.value ? new Date(field.value) : undefined}
        />
      </Card>
    </Popover>
  );
}

export default function ListLogEditModal({
  onClose,
  show,
  currentLogId,
  vehicle_id,
}: {
  onClose: Function;
  show: boolean;
  currentLogId?: string;
  vehicle_id: string;
}) {
  const { mutateAsync: createEntity, isPending: creating, isSuccess: creatingSuccess } = useMutateCreateVehiclelog();
  const { mutateAsync: updateEntity, isPending: updating, isSuccess: updateSuccess } = useUpdateVehiclelog();
  const { data: entityData, isSuccess } = useGetVehiclelog(currentLogId);

  const { addNotification } = useNotification();

  const [entity, setEntity] = useState<TypedVehicle_log>(null);

  useEffect(() => {
    if (entityData && currentLogId) {
      setEntity(entityData);
    }
  }, [entityData, currentLogId]);

  const useFields = {
    title: useField<string>({ value: entity?.title ?? '', validates: [] }),
    trip_type: useField<string>({ value: entity?.trip_type ?? '', validates: [] }),
    driver_id: useField<string>({ value: entity?.driver_id, validates: [] }),
    driver_name: useField<string>({ value: entity?.driver_data?.display_name ?? '', validates: [] }), // chỉ để hiển thị
    start_address: useField<string>({ value: entity?.start_address ?? '', validates: [] }),
    end_address: useField<string>({ value: entity?.end_address ?? '', validates: [] }),
    start_date: useField<string>({
      value: entity?.createdAt ? dayjs(new Date(Number(entity?.createdAt))).format('YYYY-MM-DD') : '',
      validates: [],
    }),
    start_time: useField<string>({ value: entity?.start_time ?? '', validates: [] }),
    end_time: useField<string>({ value: entity?.end_time ?? '', validates: [] }),
    distance: useField<string>({
      value: entity?.distance ? String(entity.distance) : '1',
      validates: [
        (inputVal) => {
          if (inputVal && Number(inputVal) > 9999) return 'Out of range.';
        },
      ],
    }),
    note: useField<string>({ value: entity?.note ?? '', validates: [] }),
  };

  useEffect(() => {
    if (show) {
      if (currentLogId && isSuccess && entityData) {
        useFields.driver_id.onChange(entityData.driver_id ?? '');
        useFields.driver_name.onChange(entityData.driver_data?.display_name ?? '');
        useFields.title.onChange(entityData.title ?? '');
        useFields.trip_type.onChange(entityData.trip_type ?? '');
        useFields.start_address.onChange(entityData.start_address ?? '');
        useFields.end_address.onChange(entityData.end_address ?? '');
        useFields.start_time.onChange(entityData.start_time ?? '');
        useFields.end_time.onChange(entityData.end_time ?? '');
        useFields.distance.onChange(entityData.distance ? String(entityData.distance) : '0');
        useFields.note.onChange(entityData.note ?? '');
      }

      if (!currentLogId) {
        helpers.resetForm(useFields);
        setEntity(null);
      }
    }
  }, [show, currentLogId, isSuccess, entityData]);

  const prevDigitsRef = useRef('');
  const handleTimeChange = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    const prevDigits = prevDigitsRef.current;
    const isAdding = digits.length > prevDigits.length;

    let formatted = raw;

    if (isAdding) {
      // chỉ format khi đang gõ thêm
      formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
    } else {
      // nếu đang xóa: giữ nguyên dấu ':' nếu nó đang có trong raw
      if (raw.includes(':')) {
        formatted = raw;
      } else {
        // fallback: format lại theo digits để tránh lộn xộn
        formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
      }
    }

    prevDigitsRef.current = digits;
    return formatted;
  };

  const { fields, submit, dirty, reset } = useForm({
    fields: useFields,
    async onSubmit(values) {
      const payload: TypedVehicle_log = {
        vehicle_id,
        driver_id: values.driver_id,
        title: values.title,
        trip_type: values.trip_type,
        start_address: values.start_address,
        end_address: values.end_address,
        start_time: values.start_time,
        end_time: values.end_time,
        distance: values.distance ? helpers.parseNumeric(values.distance) : undefined,
        note: values.note,
        createdAt: new Date(values.start_date).getTime(),
      };

      try {
        if (!currentLogId) {
          await createEntity(payload);
        } else {
          let { vehicle_id, ...updateRestData } = payload;
          await updateEntity({ id: currentLogId, ...updateRestData });
        }
        addNotification('info', 'Cập nhật thành công!');
      } catch (e) {
        addNotification('error', 'Cập nhật hoặc tạo bị lỗi!');
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }

      return { status: 'success' };
    },
  });

  useEffect(() => {
    if (updateSuccess || creatingSuccess) {
      reset();
      onClose();
    }
  }, [updateSuccess, creatingSuccess]);

  const [showAddnewUser, setShowAddnewUser] = useState(false);

  return (
    <Modal
      open={show}
      onClose={() => onClose()}
      title={currentLogId ? 'Cập nhật nhật trình' : 'Thêm nhật trình'}
      primaryAction={{
        content: currentLogId ? 'Cập nhật' : 'Tạo',
        disabled: !dirty,
        loading: updating || creating,
        onAction: submit,
      }}
      secondaryActions={[
        {
          content: 'Close',
          onAction: () => onClose(),
        },
      ]}
    >
      <Modal.Section>
        <Form onSubmit={submit}>
          <FormLayout>
            <TextField label="Tiêu đề" autoComplete="off" {...fields.title} />

            <Select
              label="Loại hành trình"
              options={helpers.enumToArray(ENUM_VEHICLE_TRIP_TYPE).map((v) => {
                return {
                  label: v,
                  value: v,
                };
              })}
              onChange={(v) => {
                fields.trip_type.onChange(v);
              }}
              value={fields.trip_type.value}
            />

            <Popover
              preferredPosition="below"
              preventCloseOnChildOverlayClick
              active={showAddnewUser}
              activator={
                <Tag onClick={() => setShowAddnewUser(true)}>
                  <InlineStack align="center" blockAlign="center">
                    <span key="add_new_assignee">Thêm lái xe</span>
                    <Icon source={PlusIcon} />
                  </InlineStack>
                </Tag>
              }
              autofocusTarget="first-node"
              onClose={() => setShowAddnewUser(false)}
            >
              <QuickSearchUser
                title=""
                user_role="sale,sale_admin,admin"
                onClose={(userData: any) => {
                  if (userData.user_id) {
                    fields.driver_id.onChange(userData.user_id);
                    fields.driver_name.onChange(userData.display_name);
                  }
                  setShowAddnewUser(false);
                }}
              />
            </Popover>

            <Text as="p">{fields.driver_name.value || 'Chưa có tài xế'}</Text>

            <TextField label="Điểm đi" autoComplete="off" {...fields.start_address} />
            <TextField label="Điểm đến" autoComplete="off" {...fields.end_address} />
            <FormLayout.Group condensed>
              <DatePickerInput label="Ngày đi" field={fields.start_date} connectedLeft={undefined} />
              <TextField
                label="Giờ Bắt đầu"
                autoComplete="off"
                {...fields.start_time}
                onChange={(v) => {
                  fields.start_time.onChange(handleTimeChange(v));
                }}
              />
              <TextField
                label="Giờ Kết thúc"
                autoComplete="off"
                {...fields.end_time}
                onChange={(v) => {
                  fields.end_time.onChange(handleTimeChange(v));
                }}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField
                label="Khoảng cách (KM)"
                autoComplete="off"
                {...fields.distance}
                value={fields.distance.value}
                onChange={(v) => {
                  fields.distance.onChange(helpers.formatNumberWithCommas(v));
                }}
              />
            </FormLayout.Group>
            <TextField label="Ghi chú" autoComplete="off" multiline={4} value={fields.note.value} onChange={fields.note.onChange} />
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  );
}

